'use client'

import { useState, useEffect, useRef } from 'react'
import { useTranslations } from '@/lib/i18n'
import { createPortal } from 'react-dom'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { CustomDropdown } from '@/components/ui/custom-dropdown'
import { useToast } from '@/hooks/use-toast'
import { updateIssue, deleteIssue } from '@/lib/issue-service'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {
  X,
  Edit3,
  Check,
  MoreVertical,
  Trash2,
  AlertTriangle,
} from 'lucide-react'
import { OrbitLoader } from '@/components/ui/orbit-loader'
import { getAuthHeaders } from '@/lib/api-utils'
import { cn } from '@/lib/utils'

interface IssueStatus {
  id: number
  name: string
  is_closed?: boolean
}

interface IssueTracker {
  id: number
  name: string
}

interface IssuePriority {
  id: number
  name: string
}

interface IssueVersion {
  id: number
  name: string
  status: string
}

interface ProjectMember {
  id: number
  name: string
  login?: string
}

interface ContextMenuData {
  statuses: IssueStatus[]
  trackers: IssueTracker[]
  priorities: IssuePriority[]
  versions: IssueVersion[]
  members: ProjectMember[]
}

interface Issue {
  id: number
  number?: number
  subject: string
  description?: string
  status: {
    id: number
    name: string
    is_closed?: boolean
  }
  priority: {
    id: number
    name: string
  }
  tracker: {
    id: number
    name: string
  }
  assigned_to?: {
    id: number
    name: string
  }
  project: {
    id: number
    name: string
  }
  fixed_version?: {
    id: number
    name: string
  }
  created_on: string
  updated_on: string
  due_date?: string
}

interface IssueActionsModalProps {
  issue: Issue
  isOpen: boolean
  onClose: () => void
  onActionComplete?: (updatedIssue?: any) => void
  onEditClick?: () => void
  showVersionMoveOnly?: boolean
  projectId?: string | number
}

export function IssueActionsModal({
  issue,
  isOpen,
  onClose,
  onActionComplete,
  onEditClick,
  showVersionMoveOnly = false,
  projectId,
}: IssueActionsModalProps): JSX.Element | null {
  const t = useTranslations('issuesPage')
  const c = useTranslations('common')
  const { toast } = useToast()
  const [menuData, setMenuData] = useState<ContextMenuData | null>(null)
  const [currentIssue, setCurrentIssue] = useState<Issue>(issue)
  const [loading, setLoading] = useState(false)
  const [mounted, setMounted] = useState(false)
  const [saving, setSaving] = useState(false)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [deleting, setDeleting] = useState(false)

  const mountedRef = useRef(false)

  // Track changes to issue properties
  const [changes, setChanges] = useState<{
    status_id?: number
    tracker_id?: number
    priority_id?: number
    assigned_to_id?: number | null
    fixed_version_id?: number | null
  }>({})

  useEffect(() => {
    setMounted(true)
    mountedRef.current = true
    return () => {
      setMounted(false)
      mountedRef.current = false
    }
  }, [])

  useEffect(() => {
    if (isOpen && issue) {
      setLoading(true)
      void (async () => {
        const freshIssue = await fetchFreshIssueData()
        await fetchMenuData(freshIssue || issue)
      })()
    } else if (!isOpen) {
      setCurrentIssue(issue)
      setLoading(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, issue?.id])

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) onClose()
    }
    if (isOpen) document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [isOpen, onClose])

  useEffect(() => {
    if (isOpen) setChanges({})
  }, [isOpen])

  const fetchFreshIssueData = async (): Promise<Issue | null> => {
    try {
      const authHeaders = getAuthHeaders()
      if (!authHeaders) throw new Error('No auth headers')

      const response = await fetch(`/api/issues/${issue.id}?_t=${Date.now()}`, {
        headers: authHeaders,
        cache: 'no-store',
      })

      if (response.ok) {
        const data = await response.json()
        if (data.issue && mountedRef.current) {
          setCurrentIssue(data.issue)
          return data.issue
        }
      }
      return null
    } catch (error) {
      console.error('[IssueActionsModal] Error fetching fresh issue data:', error)
      return null
    }
  }

  const fetchMenuData = async (issueToUse: Issue): Promise<ContextMenuData> => {
    try {
      setLoading(true)
      const authHeaders = getAuthHeaders()
      if (!authHeaders) throw new Error('No auth headers')

      const timestamp = Date.now()

      const [allowedStatusesRes, trackersRes, prioritiesRes, versionsRes, membersRes] = await Promise.allSettled([
        fetch(`/api/issues/${issueToUse.id}/allowed_statuses?_t=${timestamp}`, { headers: authHeaders, cache: 'no-store' }),
        fetch(`/api/trackers?_t=${timestamp}`, { headers: authHeaders, cache: 'no-store' }),
        fetch(`/api/enumerations/issue_priorities?_t=${timestamp}`, { headers: authHeaders, cache: 'no-store' }),
        fetch(`/api/projects/${issueToUse.project?.id}/versions?_t=${timestamp}`, { headers: authHeaders, cache: 'no-store' }),
        // Assignees are org-wide users, not project memberships — every user
        // who joins the organisation is selectable here.
        fetch(`/api/users?_t=${timestamp}`, { headers: authHeaders, cache: 'no-store' }),
      ])

      const data: ContextMenuData = {
        statuses: [],
        trackers: [],
        priorities: [],
        versions: [],
        members: [],
      }

      if (allowedStatusesRes.status === 'fulfilled' && allowedStatusesRes.value.ok) {
        const statusData = await allowedStatusesRes.value.json()
        data.statuses = statusData.allowed_statuses || []
      }

      if (trackersRes.status === 'fulfilled' && trackersRes.value.ok) {
        const trackerData = await trackersRes.value.json()
        data.trackers = trackerData.trackers || []
      }

      if (prioritiesRes.status === 'fulfilled' && prioritiesRes.value.ok) {
        const priorityData = await prioritiesRes.value.json()
        data.priorities = priorityData.issue_priorities || []
      }

      if (versionsRes.status === 'fulfilled' && versionsRes.value.ok) {
        const versionData = await versionsRes.value.json()
        data.versions = versionData.versions || []
      }

      if (membersRes.status === 'fulfilled' && membersRes.value.ok) {
        const memberData = await membersRes.value.json()
        const orgUsers = memberData.users || []
        const rawMembers = orgUsers
          .filter((u: any) => u?.id)
          .map((u: any) => ({
            id: u.id,
            name: u.name || `${u.firstname || ''} ${u.lastname || ''}`.trim() || u.login || u.email || 'Unknown',
            email: u.email || '',
            login: u.login,
          }))
        // Append email for users who share a display name to prevent ambiguity.
        const nameFreq = new Map<string, number>()
        rawMembers.forEach((m: any) => nameFreq.set(m.name, (nameFreq.get(m.name) || 0) + 1))
        data.members = rawMembers.map((m: any) => ({
          ...m,
          name: (nameFreq.get(m.name) || 0) > 1 ? `${m.name} (${m.email})` : m.name,
        }))
      }

      if (mountedRef.current) setMenuData(data)
      return data
    } catch (error) {
      console.error('[IssueActionsModal] Error fetching menu data:', error)
      throw error
    } finally {
      if (mountedRef.current) setLoading(false)
    }
  }

  const handleMenuAction = (action: string) => {
    switch (action) {
      case 'edit':
        onClose()
        onEditClick?.()
        return
      case 'copy_link':
        navigator.clipboard.writeText(`${window.location.origin}/issues/${issue.id}`)
        toast({
          title: t('linkCopiedTitle'),
          description: t('linkCopiedDescription'),
        })
        onClose()
        return
      default:
        return
    }
  }

  const handleSaveChanges = async () => {
    if (!hasChanges) return

    try {
      setSaving(true)

      // Filter out null assigned_to_id — API expects undefined for "unassign"
      const filteredChanges = { ...changes }
      if (filteredChanges.assigned_to_id === null) {
        filteredChanges.assigned_to_id = undefined
      }
      const result = await updateIssue(issue.id, { issue: filteredChanges as any })

      if (!result.success) {
        toast({
          variant: 'destructive',
          title: t('errorGeneric'),
          description: result.error || 'Failed to update issue',
        })
        setSaving(false)
        return
      }

      toast({
        title: t('issueUpdatedTitle'),
        description: t('issueUpdatedDescription'),
      })

      // Build the updated issue object with the changes applied so callers
      // can optimistically refresh without refetching.
      const updatedIssue = {
        ...currentIssue,
        ...(changes.status_id && menuData ? {
          status: menuData.statuses.find(s => s.id === changes.status_id) || currentIssue.status,
        } : {}),
        ...(changes.tracker_id && menuData ? {
          tracker: menuData.trackers.find(tr => tr.id === changes.tracker_id) || currentIssue.tracker,
        } : {}),
        ...(changes.priority_id && menuData ? {
          priority: menuData.priorities.find(p => p.id === changes.priority_id) || currentIssue.priority,
        } : {}),
        ...(changes.assigned_to_id !== undefined && menuData ? {
          assigned_to: changes.assigned_to_id
            ? menuData.members.find(m => m.id === changes.assigned_to_id) || null
            : null,
        } : {}),
        ...(changes.fixed_version_id !== undefined && menuData ? {
          fixed_version: changes.fixed_version_id
            ? menuData.versions.find(v => v.id === changes.fixed_version_id) || null
            : null,
        } : {}),
      }

      setChanges({})
      onActionComplete?.(updatedIssue)
      onClose()
    } catch (error) {
      toast({
        variant: 'destructive',
        title: t('errorGeneric'),
        description: error instanceof Error ? error.message : t('failedToSaveChanges'),
      })
    } finally {
      setSaving(false)
    }
  }

  const handleDeleteConfirm = async () => {
    setDeleting(true)
    try {
      const result = await deleteIssue(issue.id)
      if (!result.success) {
        toast({
          variant: 'destructive',
          title: t('errorGeneric'),
          description: result.error || t('failedToDelete'),
        })
        setDeleting(false)
        return
      }

      toast({
        title: t('issueDeletedTitle'),
        description: t('issueDeletedDescription', { id: String(issue.number ?? issue.id) }),
      })

      setDeleteOpen(false)
      onActionComplete?.()
      onClose()
    } catch (error) {
      toast({
        variant: 'destructive',
        title: t('errorGeneric'),
        description: error instanceof Error ? error.message : t('failedToDelete'),
      })
    } finally {
      if (mountedRef.current) setDeleting(false)
    }
  }

  const hasChanges = Object.keys(changes).length > 0

  const getCurrentValue = (field: keyof typeof changes) => {
    if (changes[field] !== undefined) return changes[field]
    switch (field) {
      case 'status_id':         return currentIssue.status?.id
      case 'tracker_id':        return currentIssue.tracker?.id
      case 'priority_id':       return currentIssue.priority?.id
      case 'assigned_to_id':    return currentIssue.assigned_to?.id || null
      case 'fixed_version_id':  return currentIssue.fixed_version?.id || null
      default:                  return null
    }
  }

  if (!mounted || !isOpen || !issue) return null

  const sidebarContent = (
    <div className="fixed inset-0 z-50">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />

      <Card className={cn(
        "absolute top-0 bottom-0 h-full w-full max-w-2xl rounded-none shadow-2xl animate-in",
        "right-0 border-l slide-in-from-right"
      )}>
        <CardContent className="flex h-full flex-col p-0">
          {/* Header */}
          <div className="flex items-center justify-between border-b px-6 py-4">
            <div className="flex items-center gap-3">
              <div className="rounded-lg bg-primary/10 p-2">
                <MoreVertical className="h-5 w-5 text-primary" />
              </div>
              <div>
                <h2 className="text-xl font-semibold">Issue Actions</h2>
                <p className="text-sm text-muted-foreground">
                  #{issue.number ?? issue.id}: {issue.subject}
                </p>
              </div>
            </div>
            <Button variant="ghost" size="icon" onClick={onClose}>
              <X className="h-5 w-5" />
            </Button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto bg-background">
            <div className="space-y-6 p-6">
              {(loading && !menuData) || !currentIssue.status ? (
                <OrbitLoader label="Loading..." className="py-8" />
              ) : (
                <>
                  {showVersionMoveOnly ? (
                    <div className="space-y-4">
                      <div className="flex items-center gap-2 pb-2 border-b">
                        <div className="h-1 w-1 rounded-full bg-primary"></div>
                        <h3 className="text-sm font-semibold text-foreground uppercase tracking-wide">
                          {t('moveToVersion')}
                        </h3>
                      </div>

                      {menuData && menuData.versions.length > 0 ? (
                        <CustomDropdown
                          label={t('fields.version')}
                          currentValue={
                            getCurrentValue('fixed_version_id')
                              ? menuData.versions.find(v => v.id === getCurrentValue('fixed_version_id'))?.name || c('noVersion')
                              : currentIssue.fixed_version?.name || c('noVersion')
                          }
                          options={[
                            { id: null, name: c('noVersion'), isSelected: getCurrentValue('fixed_version_id') === null },
                            ...menuData.versions
                              .filter(v => v.status === 'open' || v.status === 'locked')
                              .map((version) => ({
                                id: version.id,
                                name: version.name,
                                isSelected: getCurrentValue('fixed_version_id') === version.id,
                              })),
                          ]}
                          onSelect={(id) => {
                            const currentVersionId = currentIssue.fixed_version?.id || null
                            if (id !== currentVersionId) {
                              setChanges(prev => ({ ...prev, fixed_version_id: id as number | null }))
                            } else if (id === currentVersionId && changes.fixed_version_id !== undefined) {
                              const { fixed_version_id, ...rest } = changes
                              setChanges(rest)
                            }
                          }}
                        />
                      ) : (
                        <div className="space-y-1">
                          <label className="text-sm font-medium text-muted-foreground">{t('fields.version') || 'Target Version'}</label>
                          <div className="text-sm text-muted-foreground italic px-3 py-2 bg-muted/50 rounded-md">
                            No versions available for this project
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div className="flex items-center gap-2 pb-2 border-b">
                        <div className="h-1 w-1 rounded-full bg-primary"></div>
                        <h3 className="text-sm font-semibold text-foreground uppercase tracking-wide">
                          Quick Update
                        </h3>
                      </div>

                      {/* Status */}
                      {menuData && menuData.statuses.length > 0 ? (
                        <CustomDropdown
                          label={t('fields.status')}
                          currentValue={
                            menuData.statuses.find(s => s.id === getCurrentValue('status_id'))?.name || currentIssue.status?.name || ''
                          }
                          options={menuData.statuses.map((status) => ({
                            id: status.id,
                            name: status.name,
                            isSelected: getCurrentValue('status_id') === status.id,
                          }))}
                          onSelect={(id) => {
                            if (id && id !== currentIssue.status?.id) {
                              setChanges(prev => ({ ...prev, status_id: id as number }))
                            } else if (id === currentIssue.status?.id && changes.status_id !== undefined) {
                              const { status_id, ...rest } = changes
                              setChanges(rest)
                            }
                          }}
                        />
                      ) : (
                        <div className="space-y-1">
                          <label className="text-sm font-medium text-muted-foreground">{t('fields.status')}</label>
                          <div className="text-sm text-muted-foreground italic px-3 py-2 bg-muted/50 rounded-md">
                            {t('statusNoTransitions', { status: currentIssue.status?.name || 'Unknown' })}
                          </div>
                        </div>
                      )}

                      {/* Tracker */}
                      {menuData && menuData.trackers.length > 0 && (
                        <CustomDropdown
                          label={t('fields.tracker')}
                          currentValue={
                            menuData.trackers.find(tr => tr.id === getCurrentValue('tracker_id'))?.name || currentIssue.tracker?.name || ''
                          }
                          options={menuData.trackers.map((tracker) => ({
                            id: tracker.id,
                            name: tracker.name,
                            isSelected: getCurrentValue('tracker_id') === tracker.id,
                          }))}
                          onSelect={(id) => {
                            if (id && id !== currentIssue.tracker?.id) {
                              setChanges(prev => ({ ...prev, tracker_id: id as number }))
                            } else if (id === currentIssue.tracker?.id && changes.tracker_id !== undefined) {
                              const { tracker_id, ...rest } = changes
                              setChanges(rest)
                            }
                          }}
                        />
                      )}

                      {/* Priority */}
                      {menuData && menuData.priorities.length > 0 && (
                        <CustomDropdown
                          label={t('fields.priority')}
                          currentValue={
                            menuData.priorities.find(p => p.id === getCurrentValue('priority_id'))?.name || currentIssue.priority?.name || ''
                          }
                          options={menuData.priorities.map((priority) => ({
                            id: priority.id,
                            name: priority.name,
                            isSelected: getCurrentValue('priority_id') === priority.id,
                          }))}
                          onSelect={(id) => {
                            if (id && id !== currentIssue.priority?.id) {
                              setChanges(prev => ({ ...prev, priority_id: id as number }))
                            } else if (id === currentIssue.priority?.id && changes.priority_id !== undefined) {
                              const { priority_id, ...rest } = changes
                              setChanges(rest)
                            }
                          }}
                        />
                      )}

                      {/* Assignee */}
                      {menuData && (
                        <CustomDropdown
                          label={t('fields.assignee')}
                          currentValue={
                            getCurrentValue('assigned_to_id')
                              ? menuData.members.find(m => m.id === getCurrentValue('assigned_to_id'))?.name || '(nobody)'
                              : t('unassigned')
                          }
                          options={[
                            { id: null, name: t('unassigned'), isSelected: getCurrentValue('assigned_to_id') === null },
                            ...menuData.members.map((member) => ({
                              id: member.id,
                              name: member.name,
                              isSelected: getCurrentValue('assigned_to_id') === member.id,
                            })),
                          ]}
                          onSelect={(id) => {
                            const currentAssigneeId = currentIssue.assigned_to?.id || null
                            if (id !== currentAssigneeId) {
                              setChanges(prev => ({ ...prev, assigned_to_id: id as number | null }))
                            } else if (id === currentAssigneeId && changes.assigned_to_id !== undefined) {
                              const { assigned_to_id, ...rest } = changes
                              setChanges(rest)
                            }
                          }}
                        />
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Footer with Actions */}
          <div className="border-t bg-muted/30">
            {hasChanges && (
              <div className="px-6 pt-5">
                <Button
                  onClick={handleSaveChanges}
                  disabled={saving}
                  className="w-full shadow-sm"
                  size="lg"
                >
                  {saving ? (
                    <>
                      <div className="w-5 h-5 border-2 border-primary-foreground border-t-transparent rounded-full animate-spin" />
                      <span className="ml-2">{t('savingChanges')}</span>
                    </>
                  ) : (
                    <>
                      <Check className="w-4 h-4 mr-2" />
                      <span>{t('saveChanges')}</span>
                    </>
                  )}
                </Button>
              </div>
            )}

            {!showVersionMoveOnly && (
              <div className={cn("px-6 space-y-2", hasChanges ? "py-4" : "py-5")}>
                <Button
                  onClick={() => handleMenuAction('edit')}
                  variant="outline"
                  size="lg"
                  className="w-full border-2 hover:bg-primary/5 hover:border-primary/50 hover:shadow-md transition-all duration-200"
                >
                  <Edit3 className="w-4 h-4 mr-2" />
                  <span className="font-medium">{c('edit')}</span>
                </Button>
                <Button
                  onClick={() => setDeleteOpen(true)}
                  variant="ghost"
                  size="lg"
                  className="w-full text-destructive hover:text-destructive hover:bg-destructive/10 transition-all duration-200"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  <span className="font-medium">{t('deleteIssue')}</span>
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Delete confirmation */}
      <AlertDialog open={deleteOpen} onOpenChange={(open) => { if (!deleting) setDeleteOpen(open) }}>
        <AlertDialogContent className="z-[10000]">
          <AlertDialogHeader>
            <div className="flex items-center gap-3 mb-1">
              <div className="p-2 rounded-full bg-destructive/10">
                <AlertTriangle className="h-5 w-5 text-destructive" />
              </div>
              <AlertDialogTitle>{t('deleteIssueTitle')}</AlertDialogTitle>
            </div>
            <AlertDialogDescription>
              {t('deleteIssueDescription', {
                id: String(issue.number ?? issue.id),
                subject: issue.subject,
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>{c('cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); handleDeleteConfirm() }}
              disabled={deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleting ? t('deleting') : t('deleteIssue')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )

  return createPortal(sidebarContent, document.body)
}
